/**
 * /workflows/new-tenant[/:tenantId] — guided, act-per-step, resumable tenant
 * onboarding wizard (spec 0024; replaces the old collect-then-batch wizard).
 *
 * Steps: Product → Tenant basics → Branding (pick existing) → URLs → Users → Go-live.
 * Each step explains itself in plain language, performs its own API call when
 * completed, and shows a green verification once the action succeeded.
 *
 * External-auth products (spec 0069): when the selected product's
 * `authMode === 'EXTERNAL'`, its apps authenticate via their own SSO, so the
 * platform-auth surfaces don't apply — the Branding and Users steps are skipped
 * (removed from the stepper), the signup-mode field and the go-live branding
 * check are hidden. The flow becomes Product → Tenant basics → URLs → Go-live.
 *
 * Act-per-step: Step 2 fires POST /tenants immediately (then the URL gains the
 * new tenant id); Step 3 fires PUT /tenants/{id}/brand only when a brand is
 * picked (inherit = no call).
 *
 * URLs (SASADM-0168): saas-auth provisions every new tenant's SYSTEM hosts, so the
 * wizard registers no host itself. The URLs step lists those system URLs and embeds
 * the white-label domain panel (optional; its Verify DNS replaces the old DNS step).
 * Go-live checks the live app host — the active white-label APP host, else the
 * system one — and offers "Deploy this tenant's routing".
 *
 * Resumable: entering /workflows/new-tenant/{id} derives state from GETs
 * (tenant → product; tenant brand; hostnames + white-label domain; invitations)
 * and jumps to the first incomplete step with prior steps shown green.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepButton,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import type { BrandingResolution, DnsCheckResult } from '@/api/admin/hostnames';
import { ApiError } from '@/api/errors';
import { isDeployPartial, isDeployProblem } from '@/api/routingDeploy';
import { deployProblemText } from '@/components/routingDeployText';
import { MFA_POLICIES, TENANT_SIGNUP_MODES } from '@/api/types';
import type {
  BrandDto,
  CreateProductRequest,
  HostnameDto,
  MfaPolicy,
  ProductDto,
  TenantDto,
  TenantInvitationDto,
  TenantSignupMode,
  TenantUserRole,
  WhitelabelDomainDto,
} from '@/api/types';
import { getBrandsByProduct, getProducts } from '@/api/lookupCache';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { HostUrl } from '@/components/HostUrl';
import { isClickableHostType } from '@/components/hostUrlRules';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { WhitelabelDomainPanel } from '@/features/tenants/WhitelabelDomainPanel';
import { RoleMenuItems } from '@/components/RolePicker';
import { TYPE_ORDER } from '@/features/tenants/whitelabelShared';

const SLUG_REGEX = /^[a-z0-9-]+$/;

// Step indices
const STEP_PRODUCT = 0;
const STEP_BASICS = 1;
const STEP_BRANDING = 2;
const STEP_URLS = 3;
const STEP_USERS = 4;
const STEP_GOLIVE = 6;

// One editable/sent invite row in the Users step.
interface InviteRow {
  email: string;
  role: TenantUserRole;
  status: 'draft' | 'sending' | 'sent' | 'error';
  error?: ApiError;
}

interface ProductDraft {
  displayName: string;
  slug: string;
  jwtAudience: string;
  defaultMfaPolicy: MfaPolicy;
}

const EMPTY_PRODUCT_DRAFT: ProductDraft = {
  displayName: '',
  slug: '',
  jwtAudience: '',
  defaultMfaPolicy: 'OPTIONAL',
};

export function NewTenantWizard() {
  const { t } = useCopy();
  const navigate = useNavigate();
  const snack = useSnackbar();
  // SASADM-0169 — the Users step's grouped role choices (read once per render).
  const roleItems = RoleMenuItems();
  const params = useParams<{ tenantId?: string }>();
  const tenantIdParam = params.tenantId ? Number(params.tenantId) : null;

  const [active, setActive] = useState(STEP_PRODUCT);
  const [error, setError] = useState<ApiError | null>(null);

  // Step 1 — Product
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [productMode, setProductMode] = useState<'pick' | 'create'>('pick');
  const [productDraft, setProductDraft] = useState<ProductDraft>(EMPTY_PRODUCT_DRAFT);
  const [productPending, setProductPending] = useState(false);

  // Step 2 — Tenant basics
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [signupMode, setSignupMode] = useState<TenantSignupMode>('INVITE_ONLY');
  const [tenant, setTenant] = useState<TenantDto | null>(null);
  const [tenantPending, setTenantPending] = useState(false);

  // Step 3 — Branding
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [brandChoice, setBrandChoice] = useState<'inherit' | number>('inherit');
  const [brandingDone, setBrandingDone] = useState(false);
  const [brandPending, setBrandPending] = useState(false);

  // Step 4 — URLs (SASADM-0168): every hostname row + the stored white-label domain.
  const [hostnames, setHostnames] = useState<HostnameDto[]>([]);
  const [wlDomain, setWlDomain] = useState<WhitelabelDomainDto | null | undefined>(undefined);
  const [deployingTenant, setDeployingTenant] = useState(false);

  // Step 5 — Users (optional invitations)
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([
    { email: '', role: 'USER', status: 'draft' },
  ]);
  const [usersSkipped, setUsersSkipped] = useState(false);
  const [invitesPending, setInvitesPending] = useState(false);

  // Step 6 — Go-live (branding resolves + HTTPS cert issued)
  const [goLiveBrand, setGoLiveBrand] = useState<BrandingResolution | null>(null);
  const [goLiveHttps, setGoLiveHttps] = useState<DnsCheckResult | null>(null);
  const [goLiveChecking, setGoLiveChecking] = useState(false);
  const [goLiveRan, setGoLiveRan] = useState(false);

  // Derived
  const product = products.find((p) => p.id === productId) ?? null;
  // Spec 0069 — external-auth products skip the platform-auth steps/fields.
  const isExternal = product?.authMode === 'EXTERNAL';
  const productDone = productId !== '';
  const tenantDone = tenant !== null;
  // SASADM-0168 — the tenant's automatic SYSTEM hosts and its (optional) white-label domain.
  const systemRows = hostnames.filter((h) => h.source === 'SYSTEM');
  const systemApp =
    systemRows.find((h) => h.hostnameType === 'APP' && h.isPrimary) ??
    systemRows.find((h) => h.hostnameType === 'APP') ??
    null;
  const wlActive = wlDomain?.configured === true && wlDomain.state === 'ACTIVE';
  const wlApp = wlActive
    ? (wlDomain.hostnames.find(
        (h) => h.hostnameType === 'APP' && systemApp != null && h.appId === systemApp.appId,
      ) ??
      wlDomain.hostnames.find((h) => h.hostnameType === 'APP') ??
      null)
    : null;
  // The host go-live checks and the summary shows: the active white-label app host, else the system app host.
  const liveHost: string | null = wlApp?.hostname ?? systemApp?.hostname ?? null;
  const urlsDone = liveHost !== null || wlDomain?.configured === true;
  const sentInvites = inviteRows.filter((r) => r.status === 'sent');
  // Users is optional: done once at least one invite is sent, or it was skipped.
  // External-auth products have no Users step, so treat it as satisfied (spec 0069).
  const usersDone = isExternal || usersSkipped || sentInvites.length > 0;
  const allDone = productDone && tenantDone && urlsDone && usersDone;
  const assignedBrand =
    tenant?.brandId != null ? (brands.find((b) => b.id === tenant.brandId) ?? null) : null;
  const brandResolved = goLiveBrand !== null;
  const httpsOk = goLiveHttps?.httpsOk === true;
  // External-auth products have no login brand to resolve — gate go-live on HTTPS
  // alone (spec 0069).
  const goLiveDone = (isExternal || brandResolved) && httpsOk;

  // Load the product list once (fresh runs).
  useEffect(() => {
    void getProducts()
      .then(setProducts)
      .catch(() => setProducts([]));
  }, []);

  async function hydrate(id: number) {
    try {
      const tn = await apiAdmin.tenants.get(id);
      setTenant(tn);
      setSlug(tn.slug);
      setName(tn.name);
      setSignupMode(tn.signupMode);
      if (tn.productId != null) setProductId(tn.productId);

      const [[hosts, domain], brandList, invites] = await Promise.all([
        refreshUrls(id),
        tn.productId != null
          ? getBrandsByProduct(tn.productId).catch(() => [] as BrandDto[])
          : Promise.resolve([] as BrandDto[]),
        apiAdmin.invitations.list(id).catch(() => [] as TenantInvitationDto[]),
      ]);
      setBrands(brandList);
      if (tn.brandId != null) setBrandChoice(tn.brandId);
      // Branding is satisfied on resume (a brand is assigned, or the default is
      // inherited — both are valid completed states needing no further action).
      setBrandingDone(true);

      // Already-invited users show as done rows; an empty row is appended so more
      // can be added. ("skipped" is session-local — it does not persist; on resume
      // the Users step is complete iff invitations exist, else passable via Skip.)
      if (invites.length > 0) {
        setInviteRows([
          ...invites.map((i): InviteRow => ({ email: i.email, role: i.role, status: 'sent' })),
          { email: '', role: 'USER', status: 'draft' },
        ]);
      }

      // First incomplete: product (none) → URLs (no system APP host and no configured
      // domain) → go-live. Computed from the fetched values, not same-tick state.
      const hasSystemApp = hosts.some((h) => h.source === 'SYSTEM' && h.hostnameType === 'APP');
      setActive(
        tn.productId == null
          ? STEP_PRODUCT
          : !hasSystemApp && domain?.configured !== true
            ? STEP_URLS
            : STEP_GOLIVE,
      );
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    }
  }

  // SASADM-0168 — re-read every hostname row + the white-label domain. Also the
  // panel's `onChanged`, so each panel write refreshes the URLs and go-live host.
  async function refreshUrls(id: number): Promise<[HostnameDto[], WhitelabelDomainDto | null]> {
    const [hosts, domain] = await Promise.all([
      apiAdmin.hostnames.list(id).catch(() => [] as HostnameDto[]),
      apiAdmin.whitelabelDomain.get(id).catch(() => null),
    ]);
    setHostnames(hosts);
    setWlDomain(domain);
    return [hosts, domain];
  }

  // --- Step reachability + navigation -------------------------------------
  function reachable(step: number): boolean {
    if (step === STEP_PRODUCT) return true;
    if (step === STEP_BASICS) return productDone;
    if (step === STEP_BRANDING) return tenantDone;
    if (step === STEP_URLS) return tenantDone;
    if (step === STEP_USERS) return urlsDone;
    if (step === STEP_GOLIVE) return urlsDone;
    return false;
  }
  function completed(step: number): boolean {
    if (step === STEP_PRODUCT) return productDone;
    if (step === STEP_BASICS) return tenantDone;
    if (step === STEP_BRANDING) return brandingDone;
    if (step === STEP_URLS) return urlsDone;
    if (step === STEP_USERS) return usersDone;
    if (step === STEP_GOLIVE) return goLiveDone;
    return false;
  }

  // --- Step 1: product -----------------------------------------------------
  async function createProduct() {
    setError(null);
    setProductPending(true);
    try {
      const body: CreateProductRequest = {
        slug: productDraft.slug.toLowerCase(),
        displayName: productDraft.displayName.trim(),
        jwtAudience: productDraft.jwtAudience.trim(),
        defaultMfaPolicy: productDraft.defaultMfaPolicy,
        defaultMfaReverifyDays: 30,
        defaultMfaTrustedDeviceDays: 30,
      };
      const created = await apiAdmin.products.create(body);
      setProducts((prev) => [...prev, created]);
      setProductId(created.id);
      setProductMode('pick');
      snack.success(t('snackbar.productCreated', 'Product created.'));
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setProductPending(false);
    }
  }

  const productCreateValid =
    productDraft.displayName.trim().length > 0 &&
    SLUG_REGEX.test(productDraft.slug.toLowerCase()) &&
    productDraft.jwtAudience.trim().length > 0;

  // --- Step 2: tenant ------------------------------------------------------
  const basicsValid = SLUG_REGEX.test(slug) && name.trim().length > 0 && productDone;

  async function createTenant() {
    if (tenant) {
      setActive(nextStep(STEP_BASICS));
      return;
    }
    setError(null);
    setTenantPending(true);
    try {
      const tn = await apiAdmin.tenants.create({
        slug: slug.toLowerCase(),
        name: name.trim(),
        signupMode,
        productId: productId as number,
      });
      setTenant(tn);
      void refreshUrls(tn.id);
      if (typeof productId === 'number') {
        void getBrandsByProduct(productId)
          .then(setBrands)
          .catch(() => setBrands([]));
      }
      snack.success(t('snackbar.tenantCreated', 'Tenant created.'));
      setActive(nextStep(STEP_BASICS));
      navigate(`/workflows/new-tenant/${tn.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setTenantPending(false);
    }
  }

  // --- Step 3: branding ----------------------------------------------------
  async function continueBranding() {
    setError(null);
    if (typeof brandChoice === 'number' && brandChoice !== tenant?.brandId) {
      setBrandPending(true);
      try {
        const updated = await apiAdmin.tenants.setBrand(tenant!.id, brandChoice);
        setTenant(updated);
        snack.success(t('snackbar.brandAssigned', 'Brand assigned.'));
      } catch (err) {
        if (err instanceof ApiError) setError(err);
        setBrandPending(false);
        return;
      }
      setBrandPending(false);
    }
    setBrandingDone(true);
    setActive(nextStep(STEP_BRANDING));
  }

  // --- Step 4: URLs ---------------------------------------------------------
  // System rows in the Whitelabel matrix's type order.
  const orderedSystemRows = [...systemRows].sort(
    (a, b) => TYPE_ORDER.indexOf(a.hostnameType) - TYPE_ORDER.indexOf(b.hostnameType),
  );

  // Wizard-local tenant routing deploy (mirrors useRoutingDeploy.handleDeployTenant).
  async function deployTenantRouting(): Promise<boolean> {
    if (!tenant) return false;
    setError(null);
    setDeployingTenant(true);
    try {
      const res = await apiAdmin.routing.deployTenantRouting(tenant.id);
      // SASADM-0170 (SASATH-0387) — a REJECTED / FAILED deploy is not a success: report it like
      // a thrown error, so the go-live probes don't re-run. SASADM-0176 — nor is PARTIAL: a
      // warning naming the held-back slugs.
      if (isDeployPartial(res.routingDeploy)) {
        snack.warning(deployProblemText(res.routingDeploy, t));
        return false;
      }
      if (isDeployProblem(res.routingDeploy)) {
        snack.error(deployProblemText(res.routingDeploy, t));
        return false;
      }
      snack.success(t('routing.deploy.doneTenantSnack', 'Tenant routing deployed.'));
      return true;
    } catch (err) {
      // SASADM-0169 — reported exactly as the header button's handleDeployTenant does.
      if (err instanceof ApiError) {
        // SASADM-0176 — CADDY_HELD_BACK_HOST_CONFLICT (409) is reported here too.
        if (
          err.code === 'CADDY_CONFIG_INVALID' ||
          err.code === 'CADDY_RELOAD_FAILED' ||
          err.code === 'CADDY_HELD_BACK_HOST_CONFLICT'
        ) {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        } else if (err.code === 'TENANT_NOT_FOUND') {
          snack.error(t('errors.codes.TENANT_NOT_FOUND', 'Tenant not found.'));
        } else if (err.status === 403) {
          snack.error(
            t('errors.insufficientPermissions', "You don't have permission for this action."),
          );
        } else {
          snack.error(err.details.message ?? t(`errors.codes.${err.code}`, err.code));
        }
      }
      return false;
    } finally {
      setDeployingTenant(false);
    }
  }

  // --- Step 5: users (optional invitations) -------------------------------
  function updateRow(idx: number, patch: Partial<InviteRow>) {
    setInviteRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setInviteRows((rows) => [...rows, { email: '', role: 'USER', status: 'draft' }]);
  }
  function removeRow(idx: number) {
    setInviteRows((rows) => rows.filter((_, i) => i !== idx));
  }

  async function sendInvites() {
    setError(null);
    setUsersSkipped(false);
    setInvitesPending(true);
    // Send each not-yet-sent row with a non-empty email; mark per-row outcomes.
    // One failing row never blocks the others.
    const targets = inviteRows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => r.status !== 'sent' && r.email.trim().length > 0);
    await Promise.all(
      targets.map(async ({ r, i }) => {
        updateRow(i, { status: 'sending', error: undefined as unknown as ApiError });
        try {
          await apiAdmin.invitations.send(tenant!.id, {
            email: r.email.trim(),
            role: r.role,
          });
          updateRow(i, { status: 'sent' });
        } catch (err) {
          if (err instanceof ApiError) updateRow(i, { status: 'error', error: err });
        }
      }),
    );
    setInvitesPending(false);
  }

  function skipUsers() {
    setError(null);
    setUsersSkipped(true);
  }

  // --- Step 6: go-live (branding proof + HTTPS) ---------------------------
  async function runGoLive() {
    if (!liveHost) return;
    setGoLiveRan(true);
    setError(null);
    setGoLiveChecking(true);
    // External-auth products have no login brand to resolve — skip that probe
    // (spec 0069); goLiveDone gates on HTTPS alone for them.
    const [brand, https] = await Promise.all([
      isExternal
        ? Promise.resolve(null)
        : apiAdmin.hostnames.resolveBranding(liveHost).catch(() => null),
      apiAdmin.hostnames.dnsCheck(liveHost, true).catch(() => null),
    ]);
    setGoLiveBrand(brand);
    setGoLiveHttps(https);
    setGoLiveChecking(false);
  }

  // Cold-resume hydration: derive state from the server and jump to the first
  // incomplete step. Runs once, and never when the tenant was created in this
  // same session (the create handler already set `tenant` + navigated).
  // (Declared after `hydrate` / `runGoLive`, which they call — SASADM-0168.)
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (tenantIdParam == null || hydratedRef.current) return;
    if (tenant && tenant.id === tenantIdParam) {
      hydratedRef.current = true;
      return;
    }
    hydratedRef.current = true;
    void (async () => {
      await hydrate(tenantIdParam);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantIdParam, tenant]);

  // Spec 0026 — auto-run both go-live checks on entering the Go-live step.
  useEffect(() => {
    if (active === STEP_GOLIVE && tenant && !goLiveRan && !goLiveChecking) {
      void (async () => {
        await runGoLive();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tenant]);

  const STEP_KEYS = [
    { key: STEP_PRODUCT, label: t('wizards.newTenant.steps.product', 'Product') },
    { key: STEP_BASICS, label: t('wizards.newTenant.steps.basics', 'Tenant basics') },
    { key: STEP_BRANDING, label: t('wizards.newTenant.steps.branding', 'Branding') },
    { key: STEP_URLS, label: t('wizards.newTenant.steps.urls', 'URLs') },
    { key: STEP_USERS, label: t('wizards.newTenant.steps.users', 'Users') },
    { key: STEP_GOLIVE, label: t('wizards.newTenant.steps.golive', 'Go-live') },
  ];

  // Spec 0069 — external-auth products skip the platform-auth steps (Branding,
  // Users). The stepper and all forward navigation read the VISIBLE order so
  // hidden steps are never shown or navigated into. STEP_* constants are unchanged.
  const HIDDEN_WHEN_EXTERNAL = new Set<number>([STEP_BRANDING, STEP_USERS]);
  const visibleStepKeys = STEP_KEYS.filter((s) => !(isExternal && HIDDEN_WHEN_EXTERNAL.has(s.key)));
  const stepOrder = visibleStepKeys.map((s) => s.key);
  const nextStep = (current: number): number => {
    const i = stepOrder.indexOf(current);
    const next = i >= 0 ? stepOrder[i + 1] : undefined;
    return next ?? current;
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.tenants', 'Tenants'), to: '/tenants' },
          { label: t('wizards.newTenant.title', 'New tenant') },
        ]}
      />
      <Typography variant="h1" gutterBottom>
        {t('wizards.newTenant.title', 'New tenant')}
      </Typography>
      <Stepper
        activeStep={Math.max(0, stepOrder.indexOf(active))}
        nonLinear
        sx={{ mb: 4 }}
        alternativeLabel
      >
        {visibleStepKeys.map((s) => (
          <Step key={s.key} completed={completed(s.key)}>
            <StepButton onClick={() => setActive(s.key)} disabled={!reachable(s.key)}>
              {s.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <ErrorBanner error={error} />

        {active === STEP_PRODUCT && (
          <Box>
            <WhatsThis>{t('wizards.newTenant.explain.product', '')}</WhatsThis>
            {productDone && (
              <DoneAlert>
                {t('wizards.newTenant.product.selected', 'Product selected: {{name}}', {
                  name: product?.displayName ?? '',
                })}
              </DoneAlert>
            )}
            <RadioGroup
              row
              value={productMode}
              onChange={(e) => setProductMode(e.target.value as 'pick' | 'create')}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="pick"
                control={<Radio />}
                label={t('wizards.newTenant.product.pick', 'Pick an existing product')}
              />
              <FormControlLabel
                value="create"
                control={<Radio />}
                label={t('wizards.newTenant.product.create', 'Create a new product')}
              />
            </RadioGroup>

            {productMode === 'pick' ? (
              <TextField
                select
                fullWidth
                label={t('tenants.create.product', 'Product')}
                value={productId === '' ? '' : String(productId)}
                onChange={(e) => setProductId(Number(e.target.value))}
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.displayName} ({p.slug})
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  {t(
                    'wizards.newTenant.product.createHint',
                    'Rare — most tenants reuse an existing product.',
                  )}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('wizards.newTenant.product.displayName', 'Display name')}
                      value={productDraft.displayName}
                      onChange={(e) =>
                        setProductDraft({ ...productDraft, displayName: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('wizards.newTenant.product.slug', 'Slug')}
                      value={productDraft.slug}
                      onChange={(e) =>
                        setProductDraft({ ...productDraft, slug: e.target.value.toLowerCase() })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label={t('wizards.newTenant.product.jwtAudience', 'JWT audience (unique)')}
                      value={productDraft.jwtAudience}
                      onChange={(e) =>
                        setProductDraft({ ...productDraft, jwtAudience: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      select
                      fullWidth
                      label={t('wizards.newTenant.product.mfaPolicy', 'Default MFA policy')}
                      value={productDraft.defaultMfaPolicy}
                      onChange={(e) =>
                        setProductDraft({
                          ...productDraft,
                          defaultMfaPolicy: e.target.value as MfaPolicy,
                        })
                      }
                    >
                      {MFA_POLICIES.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mt: 1,
                  }}
                >
                  {t(
                    'wizards.newTenant.product.advancedNote',
                    'Advanced settings (MFA timing) use defaults — edit later in Products.',
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => void createProduct()}
                  disabled={!productCreateValid || productPending}
                  startIcon={productPending ? <CircularProgress size={16} /> : null}
                >
                  {t('wizards.newTenant.product.createSubmit', 'Create product')}
                </Button>
              </Box>
            )}

            <StepActions>
              <Button
                variant="contained"
                disabled={!productDone}
                onClick={() => setActive(nextStep(STEP_PRODUCT))}
              >
                {t('wizards.newTenant.continue', 'Continue')}
              </Button>
            </StepActions>
          </Box>
        )}

        {active === STEP_BASICS && (
          <Box>
            <WhatsThis>{t('wizards.newTenant.explain.basics', '')}</WhatsThis>
            {tenantDone && (
              <DoneAlert>
                {t('wizards.newTenant.basics.created', 'Tenant created: {{name}}', {
                  name: tenant?.name ?? '',
                })}
              </DoneAlert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('tenants.create.slug', 'Slug')}
                  value={slug}
                  disabled={tenantDone}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  error={slug.length > 0 && !SLUG_REGEX.test(slug)}
                  helperText={t(
                    'tenants.create.slugHelper',
                    "Lowercase letters, digits, hyphens. This can't be changed later.",
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('tenants.create.name', 'Name')}
                  value={name}
                  disabled={tenantDone}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              {/* Signup mode is a platform-auth (saas-auth) setting — hidden for
                  external-auth products (spec 0069). The tenant-create payload still
                  sends its default so no contract changes. */}
              {!isExternal && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    select
                    fullWidth
                    label={t('tenants.create.signupMode', 'Signup mode')}
                    value={signupMode}
                    disabled={tenantDone}
                    onChange={(e) => setSignupMode(e.target.value as TenantSignupMode)}
                  >
                    {TENANT_SIGNUP_MODES.map((m) => (
                      <MenuItem key={m} value={m}>
                        {t(`tenants.signupMode.${m}`, m)} —{' '}
                        {t(`wizards.newTenant.signupModeDesc.${m}`, '')}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
            <StepActions>
              {!tenantDone ? (
                <Button
                  variant="contained"
                  onClick={() => void createTenant()}
                  disabled={!basicsValid || tenantPending}
                  startIcon={tenantPending ? <CircularProgress size={16} /> : null}
                >
                  {t('wizards.newTenant.basics.create', 'Create tenant')}
                </Button>
              ) : (
                <Button variant="contained" onClick={() => setActive(nextStep(STEP_BASICS))}>
                  {t('wizards.newTenant.continue', 'Continue')}
                </Button>
              )}
            </StepActions>
          </Box>
        )}

        {active === STEP_BRANDING && (
          <Box>
            <WhatsThis>{t('wizards.newTenant.explain.branding', '')}</WhatsThis>
            {brandingDone && (
              <DoneAlert>
                {assignedBrand
                  ? t('wizards.newTenant.branding.using', 'Using brand: {{name}}', {
                      name: assignedBrand.displayName,
                    })
                  : t(
                      'wizards.newTenant.branding.inheriting',
                      "Inheriting the product's default brand",
                    )}
              </DoneAlert>
            )}
            <RadioGroup
              value={brandChoice === 'inherit' ? 'inherit' : 'pick'}
              onChange={(e) =>
                setBrandChoice(
                  e.target.value === 'inherit' ? 'inherit' : (brands[0]?.id ?? 'inherit'),
                )
              }
            >
              <FormControlLabel
                value="inherit"
                control={<Radio />}
                label={t(
                  'wizards.newTenant.branding.inherit',
                  "Inherit the product's default brand (recommended)",
                )}
              />
              <FormControlLabel
                value="pick"
                control={<Radio />}
                label={t('wizards.newTenant.branding.pick', 'Pick a specific brand')}
              />
            </RadioGroup>

            {brandChoice !== 'inherit' && (
              <Box sx={{ mt: 1 }}>
                {brands.length === 0 ? (
                  <Alert severity="info">
                    {t(
                      'wizards.newTenant.branding.noBrands',
                      'No brands exist for this product yet.',
                    )}
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {brands.map((b) => (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }} key={b.id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderColor: brandChoice === b.id ? 'primary.main' : 'divider',
                            borderWidth: brandChoice === b.id ? 2 : 1,
                          }}
                        >
                          <CardActionArea
                            data-testid={`brand-card-${b.id}`}
                            onClick={() => setBrandChoice(b.id)}
                            sx={{ p: 1.5 }}
                          >
                            <Box
                              sx={{
                                height: 48,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1,
                              }}
                            >
                              {b.logoUrl ? (
                                <Box
                                  component="img"
                                  src={b.logoUrl}
                                  alt=""
                                  sx={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'grey.200',
                                    borderRadius: 1,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2" align="center" noWrap>
                              {b.displayName}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
                <Box sx={{ mt: 1 }}>
                  <MuiLink component={RouterLink} to="/brands/new" variant="body2">
                    {t(
                      'wizards.newTenant.branding.editorLink',
                      'Need a new brand? Open the brand editor.',
                    )}
                  </MuiLink>
                </Box>
              </Box>
            )}

            <StepActions>
              <Button
                variant="contained"
                onClick={() => void continueBranding()}
                disabled={brandPending}
                startIcon={brandPending ? <CircularProgress size={16} /> : null}
              >
                {t('wizards.newTenant.continue', 'Continue')}
              </Button>
            </StepActions>
          </Box>
        )}

        {active === STEP_URLS && tenant && (
          <Box>
            <WhatsThis>
              {t(
                'wizards.newTenant.explain.urls',
                "Every tenant gets platform URLs automatically — they're below. You can also put it on the customer's own domain (white-label). That's optional, and you can do it later from the tenant's Whitelabel tab.",
              )}
            </WhatsThis>
            <Typography variant="subtitle2" gutterBottom>
              {t('wizards.newTenant.urls.systemTitle', 'System URLs')}
            </Typography>
            {orderedSystemRows.length > 0 ? (
              <Table
                size="small"
                aria-label={t('wizards.newTenant.urls.systemTitle', 'System URLs')}
                sx={{ mb: 3 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('hostnames.type', 'Type')}</TableCell>
                    <TableCell>{t('tenants.overview.hostnames.colUrl', 'URL')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderedSystemRows.map((h) => {
                    const typeText = t(`hostnames.types.${h.hostnameType}`, h.hostnameType);
                    const appName = h.appName ?? h.appSlug ?? null;
                    return (
                      <TableRow key={h.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {h.hostnameType === 'APP' && appName
                              ? `${typeText}: ${appName}`
                              : typeText}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <HostUrl
                            hostname={h.hostname}
                            clickable={isClickableHostType(h.hostnameType)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Alert severity="warning" sx={{ mb: 3 }}>
                {t(
                  'wizards.newTenant.urls.noSystem',
                  "This tenant has no system URLs because no base domain is configured for its product or the platform. Set the product's base domain (or the platform default), then reload this page. A white-label domain needs the system URLs to exist first.",
                )}
              </Alert>
            )}
            {/* SASADM-0169 — with no SYSTEM rows the panel has no surfaces to offer, so it is hidden. */}
            {systemRows.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  {t('wizards.newTenant.urls.whitelabelTitle', 'White-label domain (optional)')}
                </Typography>
                <WhitelabelDomainPanel
                  tenantId={tenant.id}
                  domain={wlDomain}
                  systemRows={systemRows}
                  onChanged={() => void refreshUrls(tenant.id)}
                  deployTenant={deployTenantRouting}
                  tenantDeploy={{ disabled: deployingTenant, busy: deployingTenant }}
                />
              </>
            )}
            <StepActions>
              <Button
                variant="contained"
                disabled={!urlsDone}
                onClick={() => setActive(nextStep(STEP_URLS))}
              >
                {t('wizards.newTenant.continue', 'Continue')}
              </Button>
            </StepActions>
          </Box>
        )}

        {active === STEP_USERS && (
          <Box>
            <WhatsThis>{t('wizards.newTenant.explain.users', '')}</WhatsThis>
            {usersDone && (
              <DoneAlert>
                {usersSkipped && sentInvites.length === 0
                  ? t('wizards.newTenant.users.skipped', 'Skipped — no invitations sent')
                  : t('wizards.newTenant.users.doneCount', '{{count}} invitation(s) sent', {
                      count: sentInvites.length,
                    })}
              </DoneAlert>
            )}

            <Stack spacing={1.5}>
              {inviteRows.map((row, idx) => {
                const sent = row.status === 'sent';
                const adornment = sent ? (
                  <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                ) : row.status === 'error' ? (
                  <ErrorOutlineIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                ) : null;
                return (
                  <Box key={idx}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'flex-start',
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        type="email"
                        label={t('wizards.newTenant.users.email', 'Email')}
                        value={row.email}
                        disabled={sent || row.status === 'sending'}
                        onChange={(e) => updateRow(idx, { email: e.target.value, status: 'draft' })}
                        error={row.status === 'error'}
                        helperText={
                          row.status === 'error' && row.error
                            ? t(
                                `errors.codes.${row.error.code}`,
                                row.error.details?.message ?? row.error.code,
                                {
                                  email: row.email.trim(),
                                },
                              )
                            : ''
                        }
                        {...(adornment
                          ? { slotProps: { input: { startAdornment: adornment } } }
                          : {})}
                      />
                      <TextField
                        select
                        size="small"
                        label={t('wizards.newTenant.users.role', 'Role')}
                        value={row.role}
                        disabled={sent || row.status === 'sending'}
                        onChange={(e) => updateRow(idx, { role: e.target.value as TenantUserRole })}
                        sx={{ minWidth: 140 }}
                      >
                        {roleItems}
                      </TextField>
                      {inviteRows.length > 1 && !sent ? (
                        <IconButton
                          aria-label={t('common.remove', 'Remove')}
                          onClick={() => removeRow(idx)}
                          disabled={row.status === 'sending'}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
              <Box>
                <Button onClick={addRow}>
                  {t('wizards.newTenant.users.addRow', 'Add another')}
                </Button>
              </Box>
            </Stack>

            <StepActions>
              <Stack direction="row" spacing={2}>
                {!usersDone && (
                  <Button onClick={skipUsers} disabled={invitesPending}>
                    {t('wizards.newTenant.users.skip', 'Skip')}
                  </Button>
                )}
                <Button
                  variant={usersDone ? 'outlined' : 'contained'}
                  onClick={() => void sendInvites()}
                  disabled={
                    invitesPending || !inviteRows.some((r) => r.status !== 'sent' && r.email.trim())
                  }
                  startIcon={invitesPending ? <CircularProgress size={16} /> : null}
                >
                  {t('wizards.newTenant.users.send', 'Send invitations')}
                </Button>
                {usersDone && (
                  <Button variant="contained" onClick={() => setActive(nextStep(STEP_USERS))}>
                    {t('wizards.newTenant.continue', 'Continue')}
                  </Button>
                )}
              </Stack>
            </StepActions>
          </Box>
        )}

        {active === STEP_GOLIVE && (
          <Box>
            <WhatsThis>{t('wizards.newTenant.explain.golive', '')}</WhatsThis>
            {liveHost && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
                <Typography variant="body2">
                  {t('wizards.newTenant.golive.checking', 'Checking:')}
                </Typography>
                <HostUrl hostname={liveHost} clickable />
              </Stack>
            )}
            {wlDomain?.configured && !wlActive && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t(
                  'wizards.newTenant.golive.wlDraft',
                  'The white-label domain is still in Draft, so these checks use the system URL. Verify DNS on the URLs step to switch to it.',
                )}
              </Alert>
            )}
            {goLiveDone && (
              <DoneAlert>{t('wizards.newTenant.golive.live', 'This tenant is live')}</DoneAlert>
            )}

            <Stack spacing={2}>
              {/* (a) Branding resolves — platform-auth only; external products have
                  no login brand to resolve, so this check is hidden (spec 0069). */}
              {!isExternal && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('wizards.newTenant.golive.brandingTitle', 'Branding resolves')}
                  </Typography>
                  {brandResolved ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <CheckCircleIcon color="success" fontSize="small" />
                      {goLiveBrand?.logoUrl ? (
                        <Box
                          component="img"
                          src={goLiveBrand.logoUrl}
                          alt=""
                          sx={{ height: 24, maxWidth: 80, objectFit: 'contain' }}
                        />
                      ) : null}
                      <Typography variant="body2">
                        {goLiveBrand?.displayName ?? liveHost ?? ''}
                      </Typography>
                    </Stack>
                  ) : (
                    <Alert severity="info">
                      {t(
                        'wizards.newTenant.golive.brandingWaiting',
                        'The login page is not resolving its brand at this hostname yet.',
                      )}
                    </Alert>
                  )}
                </Box>
              )}

              {/* (b) HTTPS certificate */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('wizards.newTenant.golive.httpsTitle', 'HTTPS certificate')}
                </Typography>
                {httpsOk ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2">
                      {t(
                        'wizards.newTenant.golive.httpsOk',
                        'Certificate issued — HTTPS is working',
                      )}
                    </Typography>
                  </Stack>
                ) : (
                  <Alert severity="info">
                    {t(
                      'wizards.newTenant.golive.httpsWaiting',
                      'No valid HTTPS certificate yet. The first request to a new hostname can take a few seconds while the certificate issues — re-check shortly.',
                    )}
                    {goLiveHttps?.httpsReason ? (
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        {t('wizards.newTenant.golive.httpsReason', 'Reason: {{reason}}', {
                          reason: goLiveHttps.httpsReason,
                        })}
                      </Box>
                    ) : null}
                  </Alert>
                )}
              </Box>
            </Stack>

            <StepActions>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    void (async () => {
                      const ok = await deployTenantRouting();
                      if (ok) await runGoLive();
                    })()
                  }
                  disabled={deployingTenant}
                  startIcon={deployingTenant ? <CircularProgress size={16} /> : null}
                >
                  {t('wizards.newTenant.golive.deploy', "Deploy this tenant's routing")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void runGoLive()}
                  disabled={goLiveChecking}
                  startIcon={goLiveChecking ? <CircularProgress size={16} /> : null}
                >
                  {t('wizards.newTenant.golive.recheck', 'Re-check')}
                </Button>
              </Stack>
            </StepActions>
          </Box>
        )}
      </Paper>
      {allDone && tenant && (
        <SummaryCard
          tenant={tenant}
          productName={product?.displayName ?? '—'}
          brand={assignedBrand}
          hostname={liveHost ?? ''}
          invitedEmails={sentInvites.map((r) => r.email.trim())}
          skipped={usersSkipped && sentInvites.length === 0}
          dnsState={goLiveDone ? 'live' : 'pending'}
        />
      )}
    </Box>
  );
}

function WhatsThis({ children }: { children: ReactNode }) {
  const { t } = useCopy();
  return (
    <Alert severity="info" icon={false} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">
        {t('wizards.newTenant.whatsThis', "What's this?")}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Alert>
  );
}

function DoneAlert({ children }: { children: ReactNode }) {
  return (
    <Alert severity="success" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ mb: 2 }}>
      {children}
    </Alert>
  );
}

function StepActions({ children }: { children: ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{children}</Box>
    </>
  );
}

interface SummaryCardProps {
  tenant: TenantDto;
  productName: string;
  brand: BrandDto | null;
  hostname: string;
  invitedEmails: string[];
  skipped: boolean;
  dnsState: 'pending' | 'live';
}

function SummaryCard({
  tenant,
  productName,
  brand,
  hostname,
  invitedEmails,
  skipped,
  dnsState,
}: SummaryCardProps) {
  const { t } = useCopy();
  const dnsValue =
    dnsState === 'live'
      ? t('wizards.newTenant.finish.dnsLive', 'Verified — live')
      : t('wizards.newTenant.finish.notLive', 'Not live yet');
  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }} data-testid="summary-card">
      <Typography variant="h2" gutterBottom>
        {t('wizards.newTenant.finish.title', 'Tenant is set up')}
      </Typography>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
          mb: 2,
        }}
      >
        <SummaryRow
          label={t('wizards.newTenant.finish.tenantLabel', 'Tenant')}
          value={`${tenant.name} (${tenant.slug})`}
        />
        <SummaryRow
          label={t('wizards.newTenant.finish.productLabel', 'Product')}
          value={productName}
        />
        {/* Brand: name + logo, or the inherited-default note */}
        <Grid size={{ xs: 4, sm: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('wizards.newTenant.finish.brandLabel', 'Brand')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 8, sm: 9 }}>
          {brand ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              {brand.logoUrl ? (
                <Box
                  component="img"
                  src={brand.logoUrl}
                  alt=""
                  sx={{ height: 24, maxWidth: 80, objectFit: 'contain' }}
                />
              ) : null}
              <Typography variant="body2">{brand.displayName}</Typography>
            </Stack>
          ) : (
            <Typography variant="body2">
              {t('wizards.newTenant.branding.inheriting', "Inheriting the product's default brand")}
            </Typography>
          )}
        </Grid>
        <SummaryRow
          label={t('wizards.newTenant.finish.hostnameLabel', 'Hostname')}
          value={hostname}
        />
        {/* Status — live once the go-live checks pass (SASADM-0168). */}
        <SummaryRow label={t('wizards.newTenant.finish.statusLabel', 'Status')} value={dnsValue} />
        <SummaryRow
          label={t('wizards.newTenant.finish.usersLabel', 'Invited users')}
          value={
            invitedEmails.length > 0
              ? invitedEmails.join(', ')
              : skipped
                ? t('wizards.newTenant.users.skippedShort', 'Skipped')
                : t('wizards.newTenant.users.none', 'None')
          }
        />
      </Grid>
      {dnsState !== 'live' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'wizards.newTenant.finish.finishGoLive',
            'Finish the Go-live step above to bring {{hostname}} online.',
            { hostname },
          )}
        </Alert>
      )}
      <Button component={RouterLink} to={`/tenants/${tenant.id}`} variant="contained">
        {t('wizards.newTenant.finish.viewTenant', 'Go to tenant')}
      </Button>
    </Paper>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </>
  );
}
